import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { specialties } from "@/lib/healic-data";
import { ArrowRight, Stethoscope } from "lucide-react";

const SPECIALTY_PATHS: Record<string, string> = {
  "general-surgery": "/general-surgery",
  gynaecology: "/gynecology",
  "cosmetic-gynaecology": "/specialties/cosmetic-gynaecology",
  orthopaedics: "/orthopaedics",
  urology: "/urology",
  ent: "/ent",
  ophthalmology: "/ophthalmologist",
};

export const Route = createFileRoute("/specialties/")({
  head: () => ({
    meta: [
      { title: "Medical Specialties at Healic — Surgery, Gynae, Ortho & More" },
      { name: "description", content: "Explore Healic's medical specialties: general surgery, gynaecology, orthopaedics, urology, ENT and ophthalmology." },
      { property: "og:title", content: "Healic Medical Specialties" },
      { property: "og:description", content: "Care across every major specialty with fixed pricing." },
      { property: "og:url", content: "https://healic.in/specialties" },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/specialties" }],
  }),
  component: SpecialtiesIndex,
});

function SpecialtiesIndex() {
  return (
    <PageShell>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Specialties</p>
          <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Care across every major specialty</h1>
          <p className="mt-4 max-w-2xl opacity-85">
            From planned surgeries to maternity, joint replacement and ENT — Healic specialists deliver focused
            care with transparent pricing.
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {specialties.map((s) => (
            <Link
              key={s.slug}
              to={SPECIALTY_PATHS[s.slug] ?? "/specialties"}
              className="group rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-accent hover:shadow-card"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent">
                <Stethoscope className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-display text-xl font-semibold">{s.name}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{s.short}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:text-accent">
                View details <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
